import React from 'react';
import styled from '@emotion/styled';
import { DASH_COLORS, DASH_FONTS, DASH_TYPE, MIN_TOUCH_PX } from './dashboardTokens';

// The chrome every tile shares. Tiles carry no visible title — each must be
// self-explanatory (shell decision, specs/workshop-dashboard.md); the label
// lives on as the aria-label and the detail overlay's heading. The whole
// surface is one touch target that opens the detail view, which is
// client-local by decision: opening a tile here changes nothing on any
// other device.

const Frame = styled.button`
  appearance: none;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  min-height: ${MIN_TOUCH_PX}px;
  min-width: 0;
  padding: clamp(12px, 2vmin, 48px);
  background: ${DASH_COLORS.tile};
  border: 1px solid ${DASH_COLORS.tileBorder};
  border-radius: clamp(12px, 1.4vmin, 32px);
  box-shadow: ${DASH_COLORS.tileShadow};
  backdrop-filter: blur(8px);
  color: ${DASH_COLORS.text};
  font-family: ${DASH_FONTS.ui};
  text-align: left;
  cursor: pointer;
  overflow: hidden;
  transition: background 120ms ease, border-color 120ms ease;

  &:active {
    background: ${DASH_COLORS.tilePressed};
  }
  &:focus-visible {
    outline: 3px solid ${DASH_COLORS.accent};
    outline-offset: 2px;
  }
`;

const Body = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 0;
  /* A scrollable tile (the flow bar) scrolls inside the touch target; a
     scroll gesture is not a tap, so opening the detail still works. */
  overflow-y: ${({ scroll }) => (scroll ? 'auto' : 'hidden')};
  -webkit-overflow-scrolling: touch;
  justify-content: ${({ scroll }) => (scroll ? 'flex-start' : 'center')};
`;

export const TileHeadline = styled.div`
  font-family: ${DASH_FONTS.numeric};
  font-size: ${DASH_TYPE.headline};
  font-weight: 700;
  line-height: 1;
  font-variant-numeric: tabular-nums;
  color: ${({ dim }) => (dim ? DASH_COLORS.textDim : DASH_COLORS.text)};
`;

export const TileHint = styled.div`
  font-size: ${DASH_TYPE.meta};
  color: ${DASH_COLORS.textDim};
  margin-top: 0.8vmin;
`;

export default function TileFrame({ label, onOpen, scrollBody = false, style, children }) {
  return (
    <Frame type="button" style={style} onClick={onOpen} aria-label={label} aria-haspopup="dialog">
      <Body scroll={scrollBody}>{children}</Body>
    </Frame>
  );
}
